import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import {
  ANALYTICS_EVENTS,
  trackAuthFailure,
  trackEvent,
  trackPageView,
} from '../utils/analytics';

const SITE_URL = 'https://ballotr.com';

const UpdatePassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackPageView('update_password');
  }, []);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    trackEvent(ANALYTICS_EVENTS.PASSWORD_UPDATE_SUBMITTED, {
      location: 'UpdatePassword.tsx',
      has_password: Boolean(password),
      has_confirm_password: Boolean(confirm),
    });

    if (!password || !confirm) {
      trackEvent(ANALYTICS_EVENTS.PASSWORD_UPDATE_FAILED, {
        location: 'UpdatePassword.tsx',
        reason: 'missing_fields',
      });

      toast.error('Please fill out both password fields.');
      return;
    }

    if (password !== confirm) {
      trackEvent(ANALYTICS_EVENTS.PASSWORD_UPDATE_FAILED, {
        location: 'UpdatePassword.tsx',
        reason: 'password_mismatch',
      });

      toast.error("Passwords don't match.");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.PASSWORD_UPDATE_SUCCEEDED, {
        location: 'UpdatePassword.tsx',
      });

      await supabase.auth.signOut();

      toast.success('✅ Password updated. Please sign in with your new password.');
      navigate('/auth?mode=login&reset=success', { replace: true });
      
    } catch (error: any) {
      console.error('Password reset update failed:', error);

      trackAuthFailure({
        flow: 'password_update',
        location: 'UpdatePassword.tsx',
        error,
      });

      toast.error(error?.message || 'Password update failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Update Password | Ballotr</title>
        <meta
          name="description"
          content="Set a new password for your Ballotr account after using a reset link."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/update-password`} />
      </Helmet>

      <main className="min-h-screen flex items-center justify-center bg-secondary">
        <form
          onSubmit={handleUpdate}
          className="bg-white p-6 rounded-lg shadow-lg space-y-4 w-full max-w-sm"
        >
          <h2 className="text-xl font-bold text-textPrimary text-center">Reset Your Password</h2>

          <input
            type="password"
            placeholder="New password"
            className="w-full px-4 py-2 border rounded-md"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          <input
            type="password"
            placeholder="Confirm new password"
            className="w-full px-4 py-2 border rounded-md"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />

          <button
            type="submit"
            className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-600 transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </main>
    </>
  );
};

export default UpdatePassword;